/* WHAT THE PALETTE SEARCHES.
 *
 * Kept apart from the ranking (state/search.ts) because the two change for
 * different reasons: ranking changes when the DM says a search felt wrong,
 * this changes when there is a new kind of thing to find. Adding one is a
 * function here and nothing anywhere else.
 *
 * Everything below is already in memory. Nothing is fetched to build the index,
 * which is why it can be rebuilt on a keystroke's notice and why the palette can
 * open instantly: the review's point was that this is an index and a reader, not
 * an acquisition problem.
 *
 * SCOPING. The party comes from campaignRoster, never from layout.characters
 * flat. "Who is at this table" has had ten answers in this codebase and the
 * census test exists because of it -- a palette that offers the other campaign's
 * paladin is the same bug wearing a new hat.
 */
import type { SearchItem } from "./search";
import type { Layout, Character } from "./types";
import { campaignRoster } from "./campaign";
import { masterSpellList, preparedBy } from "./ddbSpells";
import { NAV_SECTIONS } from "./types";

export interface Reference {
  rulings?: { id: string; category: string; title: string; bluf: string; details: string[] }[];
  actions?: { id: string; title: string; badge: string; text: string; rows: string[] }[];
  weaponMasteries?: { id: string; name: string; summary: string; weapons?: string[]; rules: string[] }[];
  conditionSummaries?: Record<string, string>;
  conditionRules?: Record<string, string[]>;
}

/** The 2024 glossary: the question asked most often mid-turn. */
export function conditionItems(ref: Reference): SearchItem[] {
  const summaries = ref.conditionSummaries || {};
  return Object.keys(summaries).map((name) => ({
    id: `condition:${name}`, kind: "condition", name,
    detail: summaries[name],
    body: (ref.conditionRules?.[name] || []).join(" "),
    action: { verb: "Open", nav: "Conditions" },
  }));
}

export function rulingItems(ref: Reference): SearchItem[] {
  return (ref.rulings || []).map((r) => ({
    id: `ruling:${r.id}`, kind: "ruling", name: r.title,
    detail: r.bluf, body: `${r.category} ${(r.details || []).join(" ")}`,
    action: { verb: "Open", nav: "Core rulings" },
  }));
}

export function actionItems(ref: Reference): SearchItem[] {
  return (ref.actions || []).map((a) => ({
    id: `action:${a.id}`, kind: "action", name: a.title,
    detail: a.badge, body: `${a.text} ${(a.rows || []).join(" ")}`,
    action: { verb: "Open", nav: "Actions in combat" },
  }));
}

/** The eight 2024 mastery properties, searchable by the property, its effect, OR
 *  a weapon that carries it — "which weapon has Topple?" is a one-key answer, and
 *  "what does my rapier do?" finds Vex because the weapon list is in the body. */
export function masteryItems(ref: Reference): SearchItem[] {
  return (ref.weaponMasteries || []).map((m) => ({
    id: `mastery:${m.id}`, kind: "mastery", name: m.name,
    detail: m.summary, body: `${(m.weapons || []).join(" ")} ${(m.rules || []).join(" ")}`,
    action: { verb: "Open", nav: "Weapon masteries" },
  }));
}

/** The party, scoped to this campaign. */
export function partyItems(layout: Layout): SearchItem[] {
  return campaignRoster(layout).map((c: Character) => ({
    id: `party:${c.id}`, kind: "party", name: c.name,
    /* c.detail is the sheet's own line, "Variant Human · Wizard 10", so a search
       for "wizard" finds the wizard. */
    detail: [c.detail || "", c.hp !== undefined ? `${c.hp}/${c.maxHp} hp` : "", c.ac ? `AC ${c.ac}` : ""].filter(Boolean).join(" · "),
    /* WHO CAN CAST BLESS. The prepared spellbook goes in the body, so the palette
       answers what the table actually asks, by a name rather than by opening four
       character sheets. Light and conditions for the same reason: "who has the
       lantern?" is answerable from the record already. */
    body: [
      c.concentrationOn || "",
      c.light?.name || "",
      (c.conditions || []).map((x) => (typeof x === "string" ? x : x.name || "")).join(" "),
      (c.subclasses || []).join(" "),
      (c.spellbook || []).filter((sp) => sp.prepared).map((sp) => sp.name).join(" "),
    ].join(" "),
    action: { verb: "Go", nav: "Party overview" },
  }));
}

/* The tracker row, named structurally rather than imported: ExternalEntry is
   declared inside Initiative.tsx and this module is not a reason to move it. */
interface TrackerRow { id: string; name: string; score?: string }

/** Whoever is on the tracker right now, creatures included. */
export function trackerItems(layout: Layout): SearchItem[] {
  const rows = (layout.externalInitiative || []) as TrackerRow[];
  return rows.filter((e) => e && e.name).map((e) => ({
    id: `tracker:${e.id}`, kind: "tracker", name: e.name,
    detail: e.score ? `initiative ${e.score}` : "on the tracker",
    /* A creature's stat block is one key away now that S7-2 exists. */
    action: { verb: "Stat block", statBlock: e.name, nav: "Initiative" },
  }));
}

/** The spells the account can see, from the MASTER list -- the class sweep plus
 *  everything anybody has prepared. Reading the raw catalogue here was wrong for
 *  exactly the reason S7-3 exists: the class route returns what a character
 *  knows, so a prepared caster contributes cantrips only and a Paladin
 *  contributes nothing at all.
 *
 *  requireDefinition is off: a spell whose reach did not arrive is still a spell
 *  the DM may be looking up. */
export function spellItems(layout: Layout): SearchItem[] {
  const cat = masterSpellList(layout, { requireDefinition: false });
  if (!cat) return [];
  /* Who has it prepared, from the SCOPED roster: the list of spells is not about
     who is here, but this line is. */
  const party = campaignRoster(layout);
  return cat.spells.map((s) => {
    const who = preparedBy(party, s.name);
    return {
    /* The id carries the name as well as the D&D Beyond id, because a spell that
       came only from a spellbook may have no id and they would all collide on 0. */
    id: `spell:${s.id}:${s.name}`, kind: "spell", name: s.name,
    detail: [s.level === 0 ? "Cantrip" : `Level ${s.level}`, s.school, s.concentration ? "concentration" : "", s.ritual ? "ritual" : "",
      who.length ? `prepared by ${who.join(", ")}` : ""].filter(Boolean).join(" · "),
    body: [s.save ? `${s.save} save` : "", s.attack ? "attack roll" : "", ...(s.damage || []).map((d) => `${d.dice} ${d.type}`), ...(s.tags || [])].filter(Boolean).join(" "),
    action: { verb: "Spell art", nav: "Spell art" },
    };
  });
}

/** Every page the rail can reach, so the palette navigates as well as reads. */
export function pageItems(): SearchItem[] {
  const out: SearchItem[] = [];
  for (const section of NAV_SECTIONS) {
    for (const item of section.items) {
      out.push({ id: `page:${item}`, kind: "page", name: item, detail: section.group || undefined, action: { verb: "Go", nav: item } });
    }
  }
  return out;
}

/** The DM's own entries, which are the ones they will look for by a name only
 *  they know. */
export function libraryItems(entries: { id: string; title?: string; name?: string; summary?: string; body?: string; category?: string }[] | null | undefined): SearchItem[] {
  return (entries || []).filter((e) => e && (e.title || e.name)).map((e) => ({
    id: `library:${e.id}`, kind: "library", name: String(e.title || e.name),
    detail: e.category || e.summary, body: `${e.summary || ""} ${e.body || ""}`,
    action: { verb: "Open", nav: "Core rulings" },
  }));
}

/** Everything, in one list. Deduplicated by id, because a creature can be on the
 *  tracker and in the catalogue and only needs one row. */
export function buildIndex(parts: SearchItem[][]): SearchItem[] {
  const seen = new Set<string>();
  const out: SearchItem[] = [];
  for (const part of parts) for (const item of part) {
    if (!item || !item.name || seen.has(item.id)) continue;
    seen.add(item.id); out.push(item);
  }
  return out;
}
